/**
 * MovementAnalysisContent Component
 *
 * Main orchestrator for the movement analysis feature.
 * Two-column layout: form (left) + results (right).
 *
 * Mode: Deep Analysis (Mode 3)
 */
import React, { useRef, useCallback } from 'react';
import { AnalysisForm } from './AnalysisForm';
import { AnalysisResults } from './AnalysisResults';
import { AnalysisTips } from './AnalysisTips';
import { VideoPreview } from './VideoPreview';
import { useMovementAnalysis } from '../../hooks/useMovementAnalysis';

export const MovementAnalysisContent = () => {
  const {
    isLoading,
    isSaving,
    uploadedVideo,
    setUploadedVideo,
    question,
    setQuestion,
    analysis,
    setAnalysis,
    analysisResult,
    analysisMetadata,
    error,
    notification,
    handleReset,
    handleSubmitForAnalysis,
    saveAnalysis,
  } = useMovementAnalysis();

  const videoPreviewRef = useRef(null);

  // R7: When a finding timestamp is clicked, seek video to that time
  const handleSeekToTimestamp = useCallback((timestamp) => {
    // The VideoPreview component handles seeking internally via its own ref
    // This callback is passed through to enable cross-component communication
    if (videoPreviewRef.current) {
      videoPreviewRef.current.seekToTimestamp(timestamp);
    }
  }, []);

  return (
    <div className="w-full">
      {/* Error/Notification banners */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}
      {notification && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          notification.type === 'success'
            ? 'bg-green-500/10 border border-green-500/20 text-green-400'
            : 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Analysis Form */}
        <div className="space-y-6">
          <AnalysisForm
            uploadedVideo={uploadedVideo}
            setUploadedVideo={setUploadedVideo}
            question={question}
            setQuestion={setQuestion}
            analysis={analysis}
            setAnalysis={setAnalysis}
            isLoading={isLoading}
            isSaving={isSaving}
            handleSubmitForAnalysis={handleSubmitForAnalysis}
            handleReset={handleReset}
            saveAnalysis={saveAnalysis}
          />

          {/* Video Preview with timestamp markers (R7) */}
          {uploadedVideo && analysisResult?.findings?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-zinc-400 mb-2">Video with Findings</h4>
              <VideoPreview
                file={uploadedVideo}
                findings={analysisResult.findings}
                onSeekToTimestamp={handleSeekToTimestamp}
              />
            </div>
          )}

          <AnalysisTips />
        </div>

        {/* Right Column - Results */}
        <div className="space-y-6">
          <AnalysisResults
            isLoading={isLoading}
            analysis={analysis}
            analysisResult={analysisResult}
            onSeekToTimestamp={handleSeekToTimestamp}
          />

          {/* Analysis Metadata Display */}
          {analysisMetadata && (
            <div className="bg-[#0b1118] border border-white/10 rounded-xl p-4">
              <h4 className="text-sm font-medium text-zinc-300 mb-2">Analysis Details</h4>
              <div className="text-xs text-zinc-500 space-y-1">
                <div>Model: {analysisMetadata.processingModel}</div>
                <div>Type: {analysisMetadata.analysisType}</div>
                {analysisMetadata.frameRate && <div>Frame Rate: {analysisMetadata.frameRate} FPS</div>}
                {analysisMetadata.videoClipping && (
                  <div>Clipped: {analysisMetadata.videoClipping.startOffset || '0:00'} - {analysisMetadata.videoClipping.endOffset || 'end'}</div>
                )}
                <div>Timestamps: {analysisMetadata.timestampsUsed ? 'Enabled' : 'Disabled'}</div>
                <div>Video Size: {(analysisMetadata.videoSize / 1024 / 1024).toFixed(2)}MB</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MovementAnalysisContent;
